import { FACEBOOK_API_VERSION, FACEBOOK_APP_ID } from "../constants";

/**
 * Facebook login as a full-page redirect, for phones.
 *
 * FB.login opens its dialog in a popup, and a phone with the Facebook app installed hands that
 * facebook.com URL to the app — the shop ends up in the app instead of the browser, and the app
 * cannot hand the session back to this site. Navigating this tab to the OAuth dialog keeps the
 * login in the browser; Facebook then redirects back here with the token in the URL hash.
 *
 * ⚠️ REDIRECT_URI (this site's origin + "/") must be listed under
 *    Facebook Login → Settings → "Valid OAuth Redirect URIs" of the app, or Facebook refuses.
 *
 * Same scopes and the same short-lived user token as FB.login, so everything after login
 * (fetching /me/accounts, saving page tokens) is unchanged.
 */
const STATE_KEY = "FB_WEB_LOGIN_STATE";
const RETURN_KEY = "FB_WEB_LOGIN_RETURN";
const RESULT_KEY = "FB_WEB_LOGIN_RESULT";

const redirectUri = () => `${window.location.origin}/`;

export const startFbWebLogin = ({ scope, authType }) => {
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search);
  } catch (e) {
    // without sessionStorage the result cannot be verified — fall through, capture will reject
  }
  const params = new URLSearchParams({
    client_id: FACEBOOK_APP_ID,
    redirect_uri: redirectUri(),
    response_type: "token,granted_scopes",
    scope: scope.replace(/\s+/g, ""),
    auth_type: authType,
    display: "touch",
    state,
  });
  window.location.assign(
    `https://www.facebook.com/${FACEBOOK_API_VERSION}/dialog/oauth?${params.toString()}`,
  );
};

/**
 * Run once at startup, BEFORE the router: a route redirect would drop the hash. Moves a
 * Facebook result out of the URL into sessionStorage for the Navbar to pick up.
 */
export const captureFbWebLoginRedirect = () => {
  try {
    const hash = window.location.hash || "";
    if (!hash.includes("access_token=") && !hash.includes("error=")) return;
    const p = new URLSearchParams(hash.slice(1));
    const expected = sessionStorage.getItem(STATE_KEY);
    if (!expected || p.get("state") !== expected) return; //* not ours (or forged) — ignore
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.setItem(
      RESULT_KEY,
      JSON.stringify({
        accessToken: p.get("access_token") || "",
        grantedScopes: p.get("granted_scopes") || "",
        error: p.get("error") || "",
      }),
    );
    //* token out of the address bar, and back to the page the login started from: Facebook
    //* returns to "/" (the login screen, which does not forward a signed-in user anywhere)
    const returnPath = sessionStorage.getItem(RETURN_KEY) || "/dashboard";
    window.history.replaceState(null, "", returnPath);
  } catch (e) {
    // ignore — the shop can press connect again
  }
};

/** The captured result and the path the login started from, once. */
export const takeFbWebLoginResult = () => {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RESULT_KEY);
    const returnPath = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return { ...JSON.parse(raw), returnPath };
  } catch (e) {
    return null;
  }
};
